import { useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Newspaper, ArrowRight, Eye, EyeOff } from "lucide-react";

import { useAuth } from "../auth/AuthProvider";
import { explainError } from "../api/client";

interface FormValues {
  username: string;
  password: string;
}

export function Login() {
  const { signIn, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { username: "", password: "" },
  });
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false);

  const from =
    (location.state as { from?: { pathname?: string } } | undefined)?.from
      ?.pathname ?? "/";

  // Already signed in? Bounce.
  if (status === "authenticated") {
    navigate(from === "/login" ? "/" : from, { replace: true });
  }

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setServerError(null);
    try {
      const { mustChange } = await signIn(values.username.trim(), values.password);
      navigate(mustChange ? "/change-password" : from === "/login" ? "/" : from, {
        replace: true,
      });
    } catch (e) {
      setServerError(explainError(e));
    }
  };

  return (
    <div className="min-h-screen flex flex-col paper-grain">
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-[460px] animate-fade-up">
          {/* Masthead */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <Newspaper size={18} className="text-[var(--accent)]" />
            </div>
            <h1
              className="font-display font-extrabold leading-[0.95] tracking-tight animate-ink-bleed"
              style={{
                fontSize: "clamp(48px, 8vw, 76px)",
                color: "var(--fg)",
              }}
            >
              Simpleey
              <span className="block italic font-medium text-[var(--accent)]">
                Print
              </span>
            </h1>
          </div>

          {/* Form card */}
          <div className="np-card shadow-plate">
            <div
              className="px-6 py-3 border-b"
              style={{ borderColor: "var(--rule)" }}
            >
              <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-muted)]">
                Sign In
              </span>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="px-6 py-7 flex flex-col gap-6"
              noValidate
            >
              <label className="flex flex-col gap-2">
                <span className="np-label">Username or email</span>
                <input
                  className="np-input font-display text-[20px]"
                  autoComplete="username"
                  autoFocus
                  spellCheck={false}
                  {...register("username", { required: true, minLength: 2 })}
                />
                {formState.errors.username && (
                  <span className="np-error">Username is required.</span>
                )}
              </label>

              <label className="flex flex-col gap-2">
                <span className="np-label">Password</span>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    className="np-input font-mono text-[16px] w-full pr-11"
                    autoComplete="current-password"
                    placeholder="••••••••••"
                    {...register("password", { required: true })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(s => !s)}
                    tabIndex={-1}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    title={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--fg-faint)] hover:text-[var(--fg)]"
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {formState.errors.password && (
                  <span className="np-error">Password is required.</span>
                )}
              </label>

              {serverError && (
                <div className="np-banner" role="alert">
                  <span className="font-mono text-[10px] uppercase tracking-folio mr-2 text-[var(--accent)]">
                    Notice
                  </span>
                  {serverError}
                </div>
              )}

              <button
                type="submit"
                disabled={formState.isSubmitting}
                className="np-btn w-full mt-2"
              >
                {formState.isSubmitting ? "Signing in…" : "Sign in"}
                {!formState.isSubmitting && <ArrowRight size={14} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
